import '../belege/belege.css';
import './briefe.css';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { canShowPdf, PdfPages } from '../../components/Pdf';
import { fmtDate, fmtDateTime } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { AREA_LABEL, duePill, SENDERS, senderArea } from './labels';
import { LetterKi } from './LetterKi';
import type { KanzleiLetter, LetterArea } from './types';

const IMAGE = /^image\/(jpeg|png|gif|webp)$/;
const AREA_TEAM = { fibu: 'Finanzbuchhaltung', lohn: 'Lohnbuchhaltung' } as const;

// Brief-Fenster der Kanzlei: Vorschau, Absender korrigieren, Frist eintragen, Zusammenfassung der KI, Kommentare an
// den Mandanten und „Erledigt“ (optional mit kurzer Info, die als Kommentar ankommt). Öffnen markiert den Brief für
// mich als gesehen.
export function LetterDialog({ id, access, onClose }: { id: string; access: Record<LetterArea, boolean>; onClose(): void }) {
  const toast = useToast();
  const ref = useRef<HTMLDialogElement>(null);
  const res = useApi<KanzleiLetter>(`/api/kanzlei/brief/${id}`, { live: ['briefe'] });
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const l = res.data;

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  // gesehen: beim Öffnen und wenn bei offenem Fenster ein neuer Kommentar des Mandanten ankommt
  const clientComments = l?.comments.filter((c) => c.side === 'client').length ?? 0;
  useEffect(() => {
    api.post(`/api/kanzlei/brief/${id}/read`).catch(() => {});
  }, [id, clientComments]);

  const act = async (run: () => Promise<unknown>, msg: string, close = false) => {
    setBusy(true);
    try {
      await run();
      toast(msg);
      if (close) onClose();
      else await res.reload();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const changeSender = (sender: string) => {
    if (!l || sender === l.sender) return;
    const area = senderArea(sender);
    const away = area !== l.area && !access[area];
    void act(
      () => api.post(`/api/kanzlei/brief/${id}/sender`, { sender }),
      away ? `Absender geändert · der Brief liegt jetzt bei der ${AREA_TEAM[area]}` : `Absender geändert: ${sender}`,
      away,
    );
  };

  const changeDue = (value: string) => {
    // beim Tippen der Jahreszahl entstehen Zwischenwerte wie 0202-…: erst ein vollständiges Datum speichern
    if (value && !/^(20\d{2}|2100)-\d{2}-\d{2}$/.test(value)) return;
    if (!l || (value || null) === l.due) return;
    void act(
      () => api.post(`/api/kanzlei/brief/${id}/due`, { due: value || null }),
      value ? `Frist ${fmtDate(value)} eingetragen` : 'Frist entfernt',
    );
  };

  const fileUrl = `/api/kanzlei/brief/${id}/file`;
  const pill = l?.due ? duePill(l.due) : null;
  return (
    <dialog
      ref={ref}
      id="lview"
      className="blg ltr"
      aria-label={l ? `Brief ${l.sender}: ${l.name}` : 'Brief'}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="lv-h">
        <b>{l ? `${l.sender} · Brief vom Mandanten` : 'Brief'}</b>
        <span className="note">
          {l
            ? `${l.name} · ${l.clientName} · ${l.fromOffice ? 'eingescannt' : 'hochgeladen'} am ${fmtDate(l.createdAt)}${l.createdBy ? ` von ${l.createdBy}` : ''}`
            : res.error}
        </span>
        {l && (
          <a className="btn sm" href={`${fileUrl}?download=1`} download>
            Herunterladen
          </a>
        )}
        <button type="button" className="btn sm dark" onClick={onClose} autoFocus>
          Schließen
        </button>
      </div>
      {l && (
        <>
          {IMAGE.test(l.mime) ? (
            <div className="lv-b">
              <img src={fileUrl} alt={`Brief ${l.name}`} />
            </div>
          ) : l.mime === 'application/pdf' && canShowPdf() ? (
            <object data={fileUrl} type="application/pdf" className="lv-b" aria-label={l.name}>
              <p className="note lv-np">Die PDF kann hier nicht angezeigt werden. Bitte herunterladen.</p>
            </object>
          ) : l.mime === 'application/pdf' ? (
            <div className="lv-b">
              <PdfPages
                url={fileUrl}
                title={l.name}
                fallback={<p className="note lv-np">Die PDF kann hier nicht angezeigt werden. Bitte herunterladen.</p>}
              />
            </div>
          ) : (
            <div className="lv-b">
              <p className="note lv-np">Vorschau nicht verfügbar. Bitte herunterladen.</p>
            </div>
          )}
          <div className="lv-c">
            <div className="lbar">
              {l.status === 'erledigt' ? (
                <span className="pill p-ok">
                  erledigt{l.doneAt ? ` am ${fmtDate(l.doneAt)}` : ''}
                  {l.doneBy ? ` von ${l.doneBy}` : ''}
                </span>
              ) : (
                <span className="pill p-info">offen · {AREA_LABEL[l.area]}</span>
              )}
              <label>
                Absender
                <select
                  value={l.sender}
                  disabled={busy}
                  onChange={(e) => changeSender(e.target.value)}
                  aria-label="Absender ändern"
                >
                  {SENDERS.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </label>
              {l.status === 'offen' && (
                <label>
                  Frist
                  <input
                    type="date"
                    min="2000-01-01"
                    max="2100-12-31"
                    defaultValue={l.due ?? ''}
                    key={l.due ?? ''}
                    onChange={(e) => changeDue(e.target.value)}
                    aria-label="Frist für diesen Brief"
                  />
                  {pill && <span className={`pill p-${pill.tone}`}>{pill.when}</span>}
                </label>
              )}
              <span className="grow" />
              {l.status === 'erledigt' ? (
                <button
                  type="button"
                  className="btn sm"
                  disabled={busy}
                  onClick={() => void act(() => api.post(`/api/kanzlei/brief/${id}/reopen`), 'Brief ist wieder offen')}
                >
                  Wieder öffnen
                </button>
              ) : (
                !asking && (
                  <button type="button" className="btn sm dark" disabled={busy} onClick={() => setAsking(true)}>
                    Erledigt
                  </button>
                )
              )}
            </div>
            {asking && l.status === 'offen' && (
              <DoneForm
                l={l}
                busy={busy}
                cancel={() => setAsking(false)}
                submit={(note) =>
                  act(
                    () => api.post(`/api/kanzlei/brief/${id}/done`, note ? { note } : {}),
                    `Erledigt · ${l.contactName} bekommt eine Meldung in der App`,
                    true,
                  )
                }
              />
            )}
            <LetterKi l={l} busy={busy} onDue={changeDue} onSender={changeSender} reload={res.reload} />
            <div className="note" style={{ marginBottom: 6 }}>
              <b>Kommentare zum Brief</b> · sieht {l.contactName} in der App
            </div>
            <div className="thread">
              {l.comments.map((c) => (
                <div className="cm" key={c.id}>
                  <b>{c.authorName}</b>
                  {c.side === 'client' && (
                    <>
                      {' '}
                      <span className="pill p-info">Mandant</span>
                    </>
                  )}{' '}
                  <span className="note">{fmtDateTime(c.createdAt)}</span>
                  <div>{c.body}</div>
                </div>
              ))}
              <CommentForm l={l} reload={res.reload} />
            </div>
          </div>
        </>
      )}
    </dialog>
  );
}

// „Erledigt“ mit freiwilliger kurzer Info an den Mandanten
function DoneForm({ l, busy, cancel, submit }: { l: KanzleiLetter; busy: boolean; cancel(): void; submit(note: string): void }) {
  const [note, setNote] = useState('');
  const send = (e: FormEvent) => {
    e.preventDefault();
    submit(note.trim());
  };
  return (
    <form className="ldone" onSubmit={send}>
      <label htmlFor="ld-note" className="note">
        Kurze Info an {l.contactName} (freiwillig, kommt als Kommentar)
      </label>
      <textarea
        id="ld-note"
        rows={2}
        maxLength={1000}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="z. B. Bescheid geprüft, alles korrekt. Sie müssen nichts zahlen."
        autoFocus
      />
      <div className="row">
        <button type="submit" className="btn sm dark" disabled={busy}>
          Als erledigt melden
        </button>
        <button type="button" className="btn sm" onClick={cancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function CommentForm({ l, reload }: { l: KanzleiLetter; reload(): Promise<void> }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  // Fehler stehen direkt am Eingabefeld
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/kanzlei/brief/${l.id}/comments`, { body });
      setText('');
      await reload();
      toast(`Kommentar gesendet · ${l.contactName} sieht ihn in der App`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {error && (
        <p className="note lv-err" role="alert">
          {error}
        </p>
      )}
      <form className="cform" onSubmit={submit}>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={1000}
          placeholder={`Kommentar an ${l.contactName} zu diesem Brief`}
          aria-label="Kommentar zum Brief"
        />
        <button type="submit" className="btn sm" disabled={busy}>
          Senden
        </button>
      </form>
    </>
  );
}
